import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Calculator,
  ChevronDown,
  ChevronUp,
  Eye,
  FileText,
  LoaderCircle,
  MessageSquareText,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import {
  ActuacionDocumentDialog,
  PdfViewer,
} from "./ActuacionDocumentOverlays.jsx";

const MAX_SENTENCE_FILES = 3;
const MAX_SENTENCE_BYTES = 15 * 1024 * 1024;

const RATE_NAMES = {
  0: "Banco Nación Activa",
  1: "Libre 36 meses",
  2: "Libre 60 meses",
  3: "Libre 72 meses · Ley 9516",
  4: "Banco Nación Pasiva",
  5: "Ley 4087",
  6: "Unidad de Valor Adquisitivo (UVA)",
};

const money = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 2,
});

const percentage = new Intl.NumberFormat("es-AR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

function displayDate(value) {
  if (!value) return "—";
  const [year, month, day] = String(value).split("-");
  return year && month && day ? `${day}/${month}/${year}` : String(value);
}

function InterestTrancheBreakdown({ tranche, index }) {
  return (
    <section className="workflow-interest-breakdown-tranche">
      <header>
        <span>{index + 1}</span>
        <div>
          <strong>{tranche.rateName || RATE_NAMES[tranche.rateCode] || "Tasa aplicada"}</strong>
          <small>
            {displayDate(tranche.from)} al {displayDate(tranche.to)}
            {Number.isFinite(Number(tranche.days))
              ? ` · ${tranche.days} días`
              : ""}
          </small>
        </div>
        <b>{percentage.format(Number(tranche.percentage || 0))}%</b>
      </header>
      {(tranche.details || []).length > 0 && (
        <div className="workflow-interest-breakdown-table">
          <div className="is-heading">
            <span>Período</span>
            <span>Fórmula</span>
            <span>Resultado</span>
          </div>
          {tranche.details.map((detail, detailIndex) => (
            <div key={`${detail.from}-${detail.to}-${detailIndex}`}>
              <span>
                {displayDate(detail.from)} – {displayDate(detail.to)}
              </span>
              <span>{detail.formula || "—"}</span>
              <strong>
                {percentage.format(Number(detail.percentage || 0))}%
              </strong>
            </div>
          ))}
        </div>
      )}
      <footer>
        <span>Interés del tramo</span>
        <strong>{money.format(Number(tranche.interest || 0))}</strong>
      </footer>
    </section>
  );
}

function InterestDebtBreakdown({ debt, projection = false }) {
  return (
    <article className="workflow-interest-breakdown-debt">
      <header>
        <div>
          <small>{projection ? "Proyección consolidada" : "Rubro"}</small>
          <strong>{debt.label || "Rubro sin nombre"}</strong>
        </div>
        <span>
          Capital {money.format(Number(debt.capital || 0))} · Total{" "}
          {money.format(Number(debt.total || 0))}
        </span>
      </header>
      {(debt.tranches || []).length > 0 ? (
        <div className="workflow-interest-breakdown-tranches">
          {debt.tranches.map((tranche, index) => (
            <InterestTrancheBreakdown
              tranche={tranche}
              index={index}
              key={`${tranche.rateCode}-${tranche.from}-${tranche.to}-${index}`}
            />
          ))}
        </div>
      ) : (
        <p>Este rubro no tiene intereses anteriores a la sentencia.</p>
      )}
    </article>
  );
}

function InterestBreakdown({ result }) {
  const outcomes = result.outcomes?.length
    ? result.outcomes
    : [{
        key: "result",
        label: "Resultado",
        capital: result.capital,
        interest: result.interest,
        total: result.total,
        debts: result.debts || [],
        projection: result.projection,
      }];

  return (
    <div className="workflow-interest-breakdown">
      <header className="workflow-interest-breakdown-heading">
        <div>
          <small>Detalle auditable</small>
          <strong>Cómo se realizó el cálculo</strong>
        </div>
        <span>
          Sentencia {displayDate(result.calculationDate)} · Corte{" "}
          {displayDate(result.cutoffDate)}
        </span>
      </header>
      <div className="workflow-interest-breakdown-outcomes">
        {outcomes.map((outcome) => (
          <section key={outcome.key}>
            <header>
              <strong>{outcome.label}</strong>
              <dl>
                <div>
                  <dt>Capital</dt>
                  <dd>{money.format(Number(outcome.capital || 0))}</dd>
                </div>
                <div>
                  <dt>Intereses</dt>
                  <dd>{money.format(Number(outcome.interest || 0))}</dd>
                </div>
                <div>
                  <dt>Total</dt>
                  <dd>{money.format(Number(outcome.total || 0))}</dd>
                </div>
              </dl>
            </header>
            <div className="workflow-interest-breakdown-debts">
              {(outcome.debts || []).map((debt) => (
                <InterestDebtBreakdown
                  debt={debt}
                  key={`${outcome.key}-${debt.id}`}
                />
              ))}
              {outcome.projection && (
                <InterestDebtBreakdown
                  debt={outcome.projection}
                  projection
                />
              )}
            </div>
          </section>
        ))}
      </div>
      <p className="workflow-interest-breakdown-source">
        Tasas sincronizadas desde la calculadora publicada por el Colegio de
        Abogados y Procuradores de Mendoza. Verificá la liquidación antes de
        presentarla.
      </p>
    </div>
  );
}

function todayInput() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Mendoza",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function apiDate(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function optionValue(rule) {
  return rule.calculationType === "rate"
    ? `rate:${rule.rateCode}`
    : rule.calculationType;
}

function analysisDebts(analysis) {
  return (analysis?.debts || []).map((debt) => ({
    ...debt,
    label: debt.label || "Rubro sin nombre",
    awardedCapital: (debt.awardedCapital ?? debt.capital) == null
      ? ""
      : String(debt.awardedCapital ?? debt.capital),
    rejectedCapital: debt.rejectedCapital == null
      ? ""
      : String(debt.rejectedCapital),
    includeAwarded: Number(debt.awardedCapital ?? debt.capital) > 0,
    includeRejected: Number(debt.rejectedCapital) > 0,
    rules: (debt.resolvedRules || []).map((rule) => ({
      ...rule,
      id: crypto.randomUUID(),
    })),
  }));
}

function outcomeDebt(debt, capital) {
  return {
    id: debt.id,
    label: debt.label,
    capital: Number(capital),
    manualTranches: debt.rules.map((rule) => ({
      calculationType: rule.calculationType,
      ...(rule.calculationType === "rate"
        ? { rateCode: Number(rule.rateCode) }
        : {}),
      from: apiDate(rule.from),
      to: apiDate(rule.to),
    })),
  };
}

function resultValue(calculation, documents) {
  const result = calculation.result;
  const analysis = calculation.analysis?.data;
  return {
    calculationId: calculation.id,
    status: "calculated",
    calculationDate: result.calculationDate,
    cutoffDate: result.cutoffDate,
    documents,
    summary: {
      capital: result.capital,
      interest: result.interest,
      total: result.total,
      outcomes: (result.outcomes || []).map((outcome) => ({
        key: outcome.key,
        label: outcome.label,
        capital: outcome.capital,
        interest: outcome.interest,
        total: outcome.total,
      })),
    },
    legalContext: analysis ? {
      case: analysis.case || null,
      decisions: analysis.decisions || [],
      costOrders: analysis.costOrders || [],
      warnings: analysis.warnings || [],
    } : null,
  };
}

export function WorkflowJudicialInterestField({
  api,
  templateId,
  field,
  cuij,
  value,
  onChange,
  readOnly = false,
}) {
  const [documents, setDocuments] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState(
    value?.documents || [],
  );
  const [calculation, setCalculation] = useState(null);
  const [debts, setDebts] = useState([]);
  const [calculationDate, setCalculationDate] = useState(
    value?.calculationDate || "",
  );
  const [cutoffDate, setCutoffDate] = useState(value?.cutoffDate || todayInput());
  const [preparedCalculationId, setPreparedCalculationId] = useState("");
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [starting, setStarting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [clarification, setClarification] = useState("");
  const [reconsiderationOpen, setReconsiderationOpen] = useState(false);
  const [reconsidering, setReconsidering] = useState(false);
  const [analysisPollRevision, setAnalysisPollRevision] = useState(0);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [documentDialog, setDocumentDialog] = useState(null);
  const [pdfViewer, setPdfViewer] = useState(null);
  const [documentLoading, setDocumentLoading] = useState("");
  const [attachmentLoading, setAttachmentLoading] = useState("");
  const [error, setError] = useState("");
  const uploadInputRef = useRef(null);

  const calculationId = value?.calculationId || calculation?.id || "";

  useEffect(() => {
    if (!calculationId) {
      setCalculation(null);
      setPreparedCalculationId("");
      return undefined;
    }
    let active = true;
    let timer;
    const load = async () => {
      try {
        const next = await api.getWorkflowInterestCalculation(
          templateId,
          field.id,
          calculationId,
        );
        if (!active) return;
        const analysisRunning = ["preparing", "queued", "processing"].includes(
          next.analysis?.status,
        );
        setCalculation(next);
        if (next.result && !next.draftDirty && !analysisRunning) {
          const refreshedValue = resultValue(
            next,
            value?.documents || selectedDocuments,
          );
          if (JSON.stringify(refreshedValue) !== JSON.stringify(value)) {
            onChange(refreshedValue);
          }
        }
        if (analysisRunning) {
          timer = window.setTimeout(load, 2_000);
        }
      } catch (requestError) {
        if (active) setError(requestError.message);
      }
    };
    load();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [api, calculationId, field.id, templateId, analysisPollRevision]);

  useEffect(() => {
    const analysis = calculation?.analysis?.data;
    const analysisKey = calculation?.analysis?.status === "ready"
      ? `${calculation.id}:${
        calculation.analysis.completedAt
        || calculation.analysis.clarificationUpdatedAt
        || "ready"
      }`
      : "";
    if (!analysis || !analysisKey || preparedCalculationId === analysisKey) {
      return;
    }
    setDebts(analysisDebts(analysis));
    setCalculationDate(
      value?.calculationDate
      || analysis.firstInstanceJudgmentDate
      || analysis.calculationDate
      || "",
    );
    setCutoffDate(
      value?.cutoffDate
      || analysis.suggestedCutoffDate
      || todayInput(),
    );
    setPreparedCalculationId(analysisKey);
  }, [calculation, preparedCalculationId, value?.calculationDate,
    value?.cutoffDate]);

  useEffect(() => {
    setClarification(calculation?.analysis?.clarification || "");
  }, [calculation?.analysis?.clarification]);

  useEffect(
    () => () => {
      if (pdfViewer?.url) URL.revokeObjectURL(pdfViewer.url);
    },
    [pdfViewer?.url],
  );

  const selectedCount = useMemo(() => debts.reduce((count, debt) => (
    count
    + (debt.includeAwarded ? 1 : 0)
    + (debt.includeRejected ? 1 : 0)
  ), 0), [debts]);
  const selectedSourceCount = selectedIds.length + uploadedFiles.length;

  const loadDocuments = async () => {
    if (!String(cuij || "").trim()) return;
    setLoadingDocuments(true);
    setError("");
    try {
      const result = await api.listWorkflowInterestDocuments(
        templateId,
        field.id,
        String(cuij).trim(),
      );
      setDocuments(result.items || []);
      const availableSlots = Math.max(
        0,
        MAX_SENTENCE_FILES - uploadedFiles.length,
      );
      setSelectedIds((result.items || [])
        .slice(0, availableSlots)
        .map(({ actId }) => actId));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const openDocument = async (document, attachmentId = "") => {
    if (!document?.actId || documentLoading || attachmentLoading) return;
    if (attachmentId) setAttachmentLoading(attachmentId);
    else setDocumentLoading(document.actId);
    setDocumentDialog(null);
    setError("");
    try {
      const result = await api.actuacionDocument({
        cuij: document.cuij || String(cuij).trim(),
        actId: document.actId,
        attachmentId: attachmentId || undefined,
      });
      if (result?.status === "pdf" && result.blob?.size) {
        setPdfViewer({
          url: URL.createObjectURL(result.blob),
          fileName: result.fileName || `${document.title || "sentencia"}.pdf`,
        });
        return;
      }
      if (result?.status === "select_attachment") {
        setDocumentDialog({
          kind: "attachments",
          document,
          attachments: result.attachments || [],
        });
        return;
      }
      setDocumentDialog({
        kind: "message",
        title: "PDF no disponible",
        message:
          "El Poder Judicial no informó un PDF para esta actuación.",
      });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setDocumentLoading("");
      setAttachmentLoading("");
    }
  };

  const addUploadedFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const pdfs = incoming.filter((file) => (
      file.type === "application/pdf"
      || file.name.toLocaleLowerCase().endsWith(".pdf")
    ));
    if (pdfs.length !== incoming.length) {
      setError("Solo podés adjuntar archivos PDF.");
      return;
    }
    const uploadedBytes = uploadedFiles.reduce((total, { file }) => (
      total + file.size
    ), 0);
    const incomingBytes = pdfs.reduce((total, file) => total + file.size, 0);
    if (uploadedBytes + incomingBytes > MAX_SENTENCE_BYTES) {
      setError("Los PDF subidos no pueden superar 15 MB en total.");
      return;
    }
    const availableSlots = MAX_SENTENCE_FILES - selectedSourceCount;
    if (pdfs.length > availableSlots) {
      setError("Podés analizar hasta tres sentencias en total.");
      return;
    }
    setError("");
    setUploadedFiles((current) => [
      ...current,
      ...pdfs.map((file) => ({ id: crypto.randomUUID(), file })),
    ]);
  };

  const startAnalysis = async () => {
    const chosen = documents.filter(({ actId }) => selectedIds.includes(actId));
    if (!chosen.length && !uploadedFiles.length) return;
    setStarting(true);
    setError("");
    try {
      const references = chosen.map((document) => ({
        cuij: document.cuij || String(cuij).trim(),
        actId: document.actId,
        title: document.title,
        date: document.date || null,
      }));
      const uploadedReferences = uploadedFiles.map(({ id, file }) => ({
        cuij: String(cuij || "").trim(),
        actId: `manual-${id}`,
        title: file.name.slice(0, 300),
        date: null,
      }));
      const allReferences = [...references, ...uploadedReferences];
      const created = await api.createWorkflowInterestAnalysis(templateId, {
        fieldId: field.id,
        documents: references,
        files: uploadedFiles.map(({ file }) => file),
        fallbackDate: todayInput(),
      });
      setSelectedDocuments(allReferences);
      setCalculation(created);
      setPreparedCalculationId("");
      onChange({
        calculationId: created.id,
        status: "processing",
        documents: allReferences,
      });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setStarting(false);
    }
  };

  const chooseOtherDocuments = () => {
    setCalculation(null);
    setPreparedCalculationId("");
    setDebts([]);
    setClarification("");
    setReconsiderationOpen(false);
    setSelectedDocuments([]);
    setError("");
    onChange(null);
  };

  const updateDebt = (debtId, patch) => {
    setDebts((current) => current.map((debt) => (
      debt.id === debtId ? { ...debt, ...patch } : debt
    )));
  };

  const updateRule = (debtId, ruleId, patch) => {
    setDebts((current) => current.map((debt) => (
      debt.id === debtId
        ? {
            ...debt,
            rules: debt.rules.map((rule) => (
              rule.id === ruleId ? { ...rule, ...patch } : rule
            )),
          }
        : debt
    )));
  };

  const calculate = async () => {
    const selected = debts.filter(({ includeAwarded, includeRejected }) => (
      includeAwarded || includeRejected
    ));
    const invalid = selected.find((debt) => (
      (debt.includeAwarded && !(Number(debt.awardedCapital) > 0))
      || (debt.includeRejected && !(Number(debt.rejectedCapital) > 0))
      || debt.rules.some((rule) => (
        !rule.from
        || !rule.to
        || rule.to <= rule.from
        || !rule.calculationType
        || (rule.calculationType === "rate"
          && !Number.isInteger(Number(rule.rateCode)))
      ))
    ));
    if (!selectedCount) {
      setError("Seleccioná al menos un importe para calcular.");
      return;
    }
    if (invalid) {
      setError(`Revisá los importes y períodos de “${invalid.label}”.`);
      return;
    }
    if (!calculationDate || !cutoffDate || cutoffDate <= calculationDate) {
      setError("La fecha de corte debe ser posterior a la sentencia de primera instancia.");
      return;
    }
    setCalculating(true);
    setError("");
    try {
      const input = {
        source: "sentences",
        calculationDate,
        cutoffDate,
        case: calculation.analysis.data.case || {},
        outcomes: [
          {
            key: "awarded",
            label: "Por lo que prospera",
            debts: selected.filter(({ includeAwarded }) => includeAwarded)
              .map((debt) => outcomeDebt(debt, debt.awardedCapital)),
          },
          {
            key: "rejected",
            label: "Por lo que rechaza",
            debts: selected.filter(({ includeRejected }) => includeRejected)
              .map((debt) => outcomeDebt(debt, debt.rejectedCapital)),
          },
        ].filter(({ debts: outcomeDebts }) => outcomeDebts.length),
      };
      const next = await api.calculateWorkflowInterest(
        templateId,
        calculation.id,
        { fieldId: field.id, input },
      );
      setCalculation(next);
      setShowBreakdown(false);
      onChange(resultValue(next, selectedDocuments));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCalculating(false);
    }
  };

  const reconsiderAnalysis = async () => {
    const nextClarification = clarification.trim();
    if (!nextClarification) {
      setError("Escribí una aclaración para volver a analizar las sentencias.");
      return;
    }
    setReconsidering(true);
    setError("");
    try {
      const next = await api.retryWorkflowInterestAnalysis(
        templateId,
        calculation.id,
        {
          fieldId: field.id,
          clarification: nextClarification,
        },
      );
      setCalculation(next);
      setAnalysisPollRevision((current) => current + 1);
      setPreparedCalculationId("");
      setDebts([]);
      setShowBreakdown(false);
      setReconsiderationOpen(false);
      onChange({
        calculationId: next.id,
        status: "processing",
        documents: selectedDocuments,
      });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setReconsidering(false);
    }
  };

  const result = calculation?.draftDirty ? null : calculation?.result;
  const analysisStatus = calculation?.analysis?.status;
  const primaryValue = result
    ? money.format(result.total)
    : calculationId
      ? ["preparing", "queued", "processing"].includes(analysisStatus)
        ? "Analizando sentencias…"
        : calculation?.analysis?.data
          ? "Listo para calcular"
          : "Cargando cálculo…"
      : "Sin calcular";

  return (
    <section className="workflow-record-field workflow-interest-field">
      <ActuacionDocumentDialog
        dialog={documentDialog}
        busy={attachmentLoading}
        onClose={() => setDocumentDialog(null)}
        onSelectAttachment={(attachment) => {
          if (documentDialog?.kind !== "attachments") return;
          openDocument(documentDialog.document, attachment.id);
        }}
      />
      {pdfViewer && (
        <PdfViewer
          document={pdfViewer}
          onClose={() => setPdfViewer(null)}
        />
      )}
      <span className="workflow-record-label workflow-record-automated-label">
        <Calculator size={13} />
        {field.label}{field.required && <strong> *</strong>}
      </span>
      <div className="workflow-record-calculated-control">
        <div className={`workflow-record-calculated-value${result ? " is-calculated" : ""}`}>
          <span>{primaryValue}</span>
        </div>
        {result && (
          <>
            <button
              className="workflow-record-calculated-action"
              type="button"
              aria-expanded={showBreakdown}
              aria-controls={`workflow-interest-breakdown-${field.id}`}
              onClick={() => setShowBreakdown((current) => !current)}
            >
              {showBreakdown
                ? <ChevronUp size={15} />
                : <ChevronDown size={15} />}
              {showBreakdown ? "Ocultar desglose" : "Ver desglose"}
            </button>
            {!readOnly && (
              <button
                className="workflow-record-calculated-action"
                type="button"
                onClick={() => {
                  setShowBreakdown(false);
                  setCalculation((current) => ({ ...current, result: null }));
                  onChange(null);
                }}
              >
                <RefreshCw size={15} />
                Revisar rubros
              </button>
            )}
          </>
        )}
      </div>
      {error && <p className="workflow-interest-error">{error}</p>}

      {!calculationId && !readOnly && (
        <div className="workflow-interest-documents">
          <div>
            <strong>Sentencias para analizar</strong>
            <span>
              Elegí documentos detectados o subí tus propios PDF.
            </span>
          </div>
          <button
            className="button secondary"
            type="button"
            disabled={loadingDocuments || !String(cuij || "").trim()}
            onClick={loadDocuments}
          >
            {loadingDocuments
              ? <LoaderCircle className="spin" size={16} />
              : <RefreshCw size={16} />}
            {loadingDocuments ? "Buscando…" : "Buscar sentencias"}
          </button>
          <div className="workflow-interest-upload-control">
            <input
              ref={uploadInputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              hidden
              onChange={(event) => {
                addUploadedFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <button
              className="button secondary"
              type="button"
              disabled={starting || selectedSourceCount >= MAX_SENTENCE_FILES}
              onClick={() => uploadInputRef.current?.click()}
            >
              <Upload size={16} />
              Subir PDF
            </button>
            <span>
              Podés adjuntarlos aunque hayamos encontrado sentencias.
              {` ${selectedSourceCount} de ${MAX_SENTENCE_FILES} seleccionados.`}
            </span>
          </div>
          {(documents.length > 0 || uploadedFiles.length > 0) && (
            <div className="workflow-interest-document-list">
              {documents.map((document) => {
                const checked = selectedIds.includes(document.actId);
                return (
                  <div
                    className="workflow-interest-document-item"
                    key={document.actId}
                  >
                    <label>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!checked
                          && selectedSourceCount >= MAX_SENTENCE_FILES}
                        onChange={(event) => setSelectedIds((current) => (
                          event.target.checked
                            ? [...current, document.actId]
                            : current.filter((id) => id !== document.actId)
                        ))}
                      />
                      <FileText size={16} />
                      <span>
                        <strong>{document.title}</strong>
                        <small>{document.date || "Fecha no informada"}</small>
                      </span>
                    </label>
                    <button
                      className="workflow-interest-document-view"
                      type="button"
                      disabled={Boolean(documentLoading || attachmentLoading)}
                      onClick={() => openDocument(document)}
                    >
                      {documentLoading === document.actId
                        ? <LoaderCircle className="spin" size={15} />
                        : <Eye size={15} />}
                      {documentLoading === document.actId
                        ? "Abriendo…"
                        : "Ver"}
                    </button>
                  </div>
                );
              })}
              {uploadedFiles.map(({ id, file }) => (
                <div className="workflow-interest-uploaded-file" key={id}>
                  <FileText size={16} />
                  <span>
                    <strong>{file.name}</strong>
                    <small>PDF subido manualmente</small>
                  </span>
                  <button
                    type="button"
                    aria-label={`Quitar ${file.name}`}
                    disabled={starting}
                    onClick={() => setUploadedFiles((current) => (
                      current.filter((entry) => entry.id !== id)
                    ))}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <button
                className="button primary"
                type="button"
                disabled={starting || !selectedSourceCount}
                onClick={startAnalysis}
              >
                {starting
                  ? <LoaderCircle className="spin" size={16} />
                  : <Sparkles size={16} />}
                {starting ? "Preparando…" : "Analizar sentencias"}
              </button>
            </div>
          )}
        </div>
      )}

      {calculationId && ["preparing", "queued", "processing"].includes(
        analysisStatus,
      ) && (
        <div className="workflow-interest-progress">
          <LoaderCircle className="spin" size={20} />
          <span>
            <strong>Analizando las sentencias</strong>
            <small>
              Podés guardar el registro y volver más tarde.
            </small>
          </span>
        </div>
      )}

      {analysisStatus === "failed" && (
        <div className="workflow-interest-analysis-failed">
          <p className="workflow-interest-error">
            {calculation.analysis?.error?.message
              || "No pudimos analizar las sentencias."}
          </p>
          {!readOnly && (
            <button
              className="button secondary"
              type="button"
              onClick={chooseOtherDocuments}
            >
              <Upload size={16} />
              Elegir otros PDF
            </button>
          )}
        </div>
      )}

      {analysisStatus === "ready"
        && calculation?.analysis?.data?.warnings?.length > 0
        && !result
        && !readOnly && (
        <div className="workflow-interest-warnings" role="status">
          {calculation.analysis.data.warnings.map((warning, index) => (
            <p key={`${warning.code || "warning"}-${index}`}>
              <AlertTriangle size={15} />
              <span>{warning.message}</span>
            </p>
          ))}
        </div>
      )}

      {analysisStatus === "ready"
        && calculation?.analysis?.data && !result && readOnly && (
        <div className="workflow-interest-readonly-pending">
          <strong>Análisis listo para calcular</strong>
          <span>Usá Editar para revisar los rubros y completar el cálculo.</span>
        </div>
      )}

      {analysisStatus === "ready"
        && calculation?.analysis?.data && !result && !readOnly && (
        <div className="workflow-interest-review">
          <div className="workflow-interest-review-source">
            <span>
              ¿Los documentos no son correctos? Podés elegir o subir otros.
            </span>
            <button
              className="button secondary"
              type="button"
              onClick={chooseOtherDocuments}
            >
              <Upload size={15} />
              Cambiar PDF
            </button>
          </div>
          <div className="workflow-interest-reconsideration">
            <div className="workflow-interest-reconsideration-header">
              <MessageSquareText size={18} />
              <span>
                <strong>
                  {calculation.analysis?.clarification
                    ? "Aclaración aplicada al análisis"
                    : "¿Interpretás algún punto de otra manera?"}
                </strong>
                <small>
                  {calculation.analysis?.clarification
                    ? "Podés editarla y pedir una nueva lectura de las sentencias."
                    : "Agregá una aclaración profesional para reconsiderar los rubros identificados."}
                </small>
              </span>
              <button
                className="button secondary"
                type="button"
                aria-expanded={reconsiderationOpen}
                aria-controls={`workflow-interest-reconsideration-${field.id}`}
                onClick={() => setReconsiderationOpen((open) => !open)}
              >
                {reconsiderationOpen
                  ? "Cerrar"
                  : calculation.analysis?.clarification
                    ? "Editar aclaración"
                    : "Aclarar o reconsiderar"}
              </button>
            </div>
            {reconsiderationOpen && (
              <div
                className="workflow-interest-reconsideration-form"
                id={`workflow-interest-reconsideration-${field.id}`}
              >
                <label htmlFor={`workflow-interest-clarification-${field.id}`}>
                  Aclaración o criterio a reconsiderar
                  <textarea
                    id={`workflow-interest-clarification-${field.id}`}
                    value={clarification}
                    maxLength={4_000}
                    rows={4}
                    autoFocus
                    disabled={reconsidering}
                    placeholder="Ej.: La Cámara modificó solamente el rubro incapacidad; el daño moral quedó confirmado con el capital de primera instancia."
                    onChange={(event) => {
                      setClarification(event.target.value);
                      setError("");
                    }}
                  />
                </label>
                <div>
                  <small>
                    {clarification.length.toLocaleString("es-AR")} / 4.000
                  </small>
                  <button
                    className="button primary"
                    type="button"
                    disabled={reconsidering || !clarification.trim()}
                    onClick={reconsiderAnalysis}
                  >
                    {reconsidering
                      ? <LoaderCircle className="spin" size={16} />
                      : <Sparkles size={16} />}
                    {reconsidering
                      ? "Volviendo a analizar…"
                      : "Volver a analizar con esta aclaración"}
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="workflow-interest-dates">
            <label>
              Fecha de sentencia de primera instancia
              <input
                type="date"
                value={calculationDate}
                onChange={(event) => setCalculationDate(event.target.value)}
              />
            </label>
            <label>
              Fecha de corte
              <input
                type="date"
                value={cutoffDate}
                onChange={(event) => setCutoffDate(event.target.value)}
              />
            </label>
          </div>

          <div className="workflow-interest-debts">
            {debts.map((debt) => (
              <article key={debt.id}>
                <header>
                  <input
                    type="text"
                    value={debt.label}
                    onChange={(event) => updateDebt(debt.id, {
                      label: event.target.value,
                    })}
                  />
                  <small>{Math.round(Number(debt.confidence || 0) * 100)}% confianza</small>
                </header>
                <div className="workflow-interest-amounts">
                  {[
                    ["awardedCapital", "Por lo que prospera"],
                    ["rejectedCapital", "Por lo que rechaza"],
                  ].map(([key, label]) => (
                    <label key={key}>
                      {label}
                      <span><b>$</b><input
                        inputMode="decimal"
                        value={debt[key]}
                        onChange={(event) => updateDebt(debt.id, {
                          [key]: event.target.value,
                        })}
                      /></span>
                    </label>
                  ))}
                </div>
                <div className="workflow-interest-outcomes">
                  <label>
                    <input
                      type="checkbox"
                      checked={debt.includeAwarded}
                      disabled={!(Number(debt.awardedCapital) > 0)}
                      onChange={(event) => updateDebt(debt.id, {
                        includeAwarded: event.target.checked,
                      })}
                    />
                    Incluir en “Por lo que prospera”
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={debt.includeRejected}
                      disabled={!(Number(debt.rejectedCapital) > 0)}
                      onChange={(event) => updateDebt(debt.id, {
                        includeRejected: event.target.checked,
                      })}
                    />
                    Incluir en “Por lo que rechaza”
                  </label>
                </div>
                <div className="workflow-interest-rules">
                  {debt.rules.map((rule) => (
                    <div key={rule.id}>
                      <select
                        value={optionValue(rule)}
                        onChange={(event) => {
                          const next = event.target.value;
                          updateRule(debt.id, rule.id, next.startsWith("rate:")
                            ? {
                                calculationType: "rate",
                                rateCode: Number(next.slice(5)),
                              }
                            : { calculationType: next, rateCode: null });
                        }}
                      >
                        <option value="value-debt">Deuda de valor</option>
                        <option value="money-debt">Deuda de dinero</option>
                        {Object.entries(RATE_NAMES).map(([code, name]) => (
                          <option key={code} value={`rate:${code}`}>{name}</option>
                        ))}
                      </select>
                      <input
                        type="date"
                        value={rule.from || ""}
                        onChange={(event) => updateRule(debt.id, rule.id, {
                          from: event.target.value,
                        })}
                      />
                      <span>hasta</span>
                      <input
                        type="date"
                        value={rule.to || ""}
                        onChange={(event) => updateRule(debt.id, rule.id, {
                          to: event.target.value,
                        })}
                      />
                      <button
                        type="button"
                        aria-label="Eliminar período"
                        onClick={() => updateDebt(debt.id, {
                          rules: debt.rules.filter(({ id }) => id !== rule.id),
                        })}
                      ><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => updateDebt(debt.id, {
                      rules: [...debt.rules, {
                        id: crypto.randomUUID(),
                        calculationType: "value-debt",
                        rateCode: null,
                        from: debt.interestStartDate || "",
                        to: calculationDate,
                      }],
                    })}
                  ><Plus size={14} /> Agregar período</button>
                </div>
              </article>
            ))}
          </div>

          <button
            className="button secondary workflow-interest-calculate"
            type="button"
            disabled={calculating || !selectedCount}
            onClick={calculate}
          >
            {calculating
              ? <LoaderCircle className="spin" size={17} />
              : <Calculator size={17} />}
            {calculating
              ? "Calculando…"
              : `Calcular ${selectedCount} ${selectedCount === 1 ? "importe" : "importes"}`}
          </button>
        </div>
      )}

      {result && (
        <>
          <div className="workflow-interest-result">
            {(result.outcomes || []).map((outcome) => (
              <dl key={outcome.key}>
                <div><dt>{outcome.label}</dt><dd>{money.format(outcome.total)}</dd></div>
                <div><dt>Capital</dt><dd>{money.format(outcome.capital)}</dd></div>
                <div><dt>Intereses</dt><dd>{money.format(outcome.interest)}</dd></div>
              </dl>
            ))}
          </div>
          {showBreakdown && (
            <div id={`workflow-interest-breakdown-${field.id}`}>
              <InterestBreakdown result={result} />
            </div>
          )}
        </>
      )}
    </section>
  );
}
